export type TravelStayBoundary = {
  checkInDate: string;
  checkOutDate: string;
  nights: number;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function safeTimeZone(value: string | null | undefined): string {
  const candidate = String(value ?? '').trim() || 'Europe/Rome';
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: candidate }).format(new Date(0));
    return candidate;
  } catch {
    return 'Europe/Rome';
  }
}

/**
 * Restituisce la data civile di un istante nel fuso dichiarato dalla fonte.
 * Non usa il fuso del processo/browser: un soggiorno resta 4 -> 6 novembre
 * anche attraversando un cambio d'ora o aprendolo da un dispositivo estero.
 */
export function travelCivilDate(
  value: Date | string,
  timeZone?: string | null,
): string | null {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: safeTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const result = `${values.year}-${values.month}-${values.day}`;
  return ISO_DATE.test(result) ? result : null;
}

function dateOrdinal(value: string): number | null {
  if (!ISO_DATE.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const timestamp = Date.UTC(year, month - 1, day);
  const roundTrip = new Date(timestamp);
  if (roundTrip.getUTCFullYear() !== year
    || roundTrip.getUTCMonth() !== month - 1
    || roundTrip.getUTCDate() !== day) return null;
  return Math.floor(timestamp / 86_400_000);
}

/**
 * Un soggiorno usa [check-in, check-out): la data di check-out e' esclusiva.
 * Il numero di notti e' quindi la differenza fra date civili, non fra ore.
 */
export function travelStayBoundary(
  start: Date | string,
  endExclusive: Date | string,
  timeZone?: string | null,
): TravelStayBoundary | null {
  const checkInDate = travelCivilDate(start, timeZone);
  const checkOutDate = travelCivilDate(endExclusive, timeZone);
  if (!checkInDate || !checkOutDate) return null;
  const startOrdinal = dateOrdinal(checkInDate);
  const endOrdinal = dateOrdinal(checkOutDate);
  if (startOrdinal === null || endOrdinal === null || endOrdinal <= startOrdinal) return null;
  return { checkInDate, checkOutDate, nights: endOrdinal - startOrdinal };
}

/** Date civili realmente occupate: include il check-in, esclude il check-out. */
export function travelStayCoveredDates(
  start: Date | string,
  endExclusive: Date | string,
  timeZone?: string | null,
): string[] {
  const stay = travelStayBoundary(start, endExclusive, timeZone);
  if (!stay) return [];
  const startOrdinal = dateOrdinal(stay.checkInDate);
  if (startOrdinal === null) return [];
  return Array.from({ length: stay.nights }, (_, offset) =>
    new Date((startOrdinal + offset) * 86_400_000).toISOString().slice(0, 10));
}

export function isTravelStayLike(value: {
  inviteSource?: string | null;
  timeResolution?: string | null;
  travelItemType?: string | null;
} | null | undefined): boolean {
  return value?.travelItemType === 'hotel'
    || (value?.inviteSource === 'travel_email' && value?.timeResolution === 'all_day');
}
